// import config from 'src/config';
import { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { useNavigate, getRouteApi } from '@tanstack/react-router';
import Grid from '@mui/material/Grid';
import { useMaterialUIController, setMiniSidenav } from 'src/md-context';
import MDBox from 'src/components/md/MDBox';
import DashboardLayout from 'src/layouts/dashboard/DashboardLayout';

import {
  PackageIcon,
  FunnelIcon,
  WarehouseIcon,
  UsersIcon,
  UsersThreeIcon,
  GearIcon,
  EnvelopeIcon,
  KeyIcon,
  BugIcon,
  TimerIcon,
  ShoppingBagIcon,
  CubeIcon,
  BellIcon,
  Icon,
} from '@phosphor-icons/react';
import {
  PageNavigator,
  NavigationSection,
} from 'src/components/common/PageNavigator';
import SettingsCardContainer from './components/SettingsCardContainer';

import { ModulesConfig } from './components/cards/integrations/ModulesConfig';
import MembersCard from './components/cards/members/MembersCard';
import TeamsCard from './components/cards/teams/TeamsCard';
// import CategoriesCard from './components/cards/categories/CategoriesCard';
import ProductsCard from './components/cards/products/ProductsCard';
import CoreEnumsCard from './components/cards/core-enums/CoreEnumsCard';
import CoreCustomFieldsCard from './components/cards/core-custom-fields/CoreCustomFieldsCard';

import SettingsItem from './SettingsItem';
import { AuthContext } from '@/context/auth.context';

// For categories
import useCategories from 'src/api/inventory-management/useCategories';
import CreateCategoryModal from './components/cards/categories/CreateCategoryModal';

// For warehouses
import useWarehouses from 'src/api/inventory-management/useWarehouses';
import WarehousesCard from './components/cards/warehouses/WarehousesCard';
import CreateWarehouseModal from './components/cards/warehouses/CreateWarehouseModal';
import OrganizationSettingsCard from './components/cards/organization-config/OrganizationSettingsCard';
import AzureEmailIngestCard from './components/cards/organization-config/AzureEmailIngestCard';
import { KnownDomainsCard } from './components/cards/known-domains/KnownDomainsCard';
import OrderfulEdiConfigCard from './components/cards/orderful-edi/OrderfulEdiConfigCard';
import FamousConfigCard from './components/cards/famous/FamousConfigCard';
import {
  ProductModule,
  useIsModuleEnabled,
} from '@/hooks/organization-config/useOrganizationEnabledModules';
import { isAdmin } from '@/utils/user';
import { useViewMode } from '@/context/view-mode.context';
import useTestTimeout from 'src/api/qa/useTestTimeout';
import TextField from '@mui/material/TextField';
import MDButton from 'src/components/md/MDButton';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';

function SettingsOrganizationPage() {
  const settingsOrganizationRoute = getRouteApi(
    '/authenticated/settings/organization',
  );
  const search = settingsOrganizationRoute.useSearch();
  const navigate = useNavigate({ from: '/settings/organization' });
  const activeItem =
    ((search as Record<string, unknown>).section as string | undefined) ||
    'products';
  const { currentUser } = useContext(AuthContext);

  const [, dispatch] = useMaterialUIController();
  const categories = useCategories({});
  const [showCreateCategoryModal, setShowCreateCategoryModal] =
    useState<boolean>(false);
  const { isModuleEnabled } = useIsModuleEnabled();

  const handleCloseCreateCategoryModal = (refetch?: boolean) => {
    if (refetch) {
      categories.refetch();
    }
    setShowCreateCategoryModal(false);
  };

  const [showCreateWarehouseModal, setShowCreateWarehouseModal] =
    useState<boolean>(false);
  const warehouses = useWarehouses();

  const handleCloseCreateWarehouseModal = (refetch?: boolean) => {
    if (refetch) {
      warehouses.refetch();
    }
    setShowCreateWarehouseModal(false);
  };

  // Add Gently AI specific items if user has @gently-ai.com email and is in admin view
  const { viewMode } = useViewMode();
  const gently = isAdmin(currentUser, viewMode);

  const testTimeout = useTestTimeout();

  const [seconds, setSeconds] = useState<string>('2');

  const [milliseconds, setMilliseconds] = useState<string>('500');

  useEffect(() => {
    // TODO disable for now since titles don't shrink
    if (Math.random() > 2) {
      setMiniSidenav(dispatch, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleItemClick = (href: string) => {
    navigate({ to: '.', search: { section: href } });
  };

  // Build settings items array programmatically
  const items: SettingsItem[] = [];

  // Helper function to map icon names to Lucide icons
  const getIconComponent = (iconName: string): Icon => {
    const iconMap: Record<string, Icon> = {
      grid_view: PackageIcon,
      edit_attributes: FunnelIcon,
      list: CubeIcon,
      warehouse: WarehouseIcon,
      person: UsersIcon,
      diversity_3: UsersThreeIcon,
      settings: GearIcon,
      email: EnvelopeIcon,
      vpn_key: KeyIcon,
      bug_report: BugIcon,
      timer: TimerIcon,
      inventory: ShoppingBagIcon,
      bell: BellIcon,
    };
    return iconMap[iconName] || GearIcon;
  };

  const queryClient = useQueryClient();

  const reseedNotifications = useMutation({
    mutationFn: async () => {
      return await rpcClient.demo.notifications.reseed({});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.demo.notifications.list.queryOptions().queryKey,
      });
    },
  });

  // Add base items
  items.push(
    {
      indent: false,
      icon: 'grid_view',
      title: 'Products',
      subtitle: 'Manage your product catalog',
      href: 'products',
      content: <ProductsCard />,
    },
    {
      indent: false,
      icon: 'edit_attributes',
      title: 'Custom Fields',
      subtitle: 'Manage your core custom fields',
      href: 'core-custom-fields',
      content: <CoreCustomFieldsCard />,
    },
    {
      indent: false,
      icon: 'list',
      title: 'Enums',
      subtitle: 'Manage your core enums',
      href: 'core-enums',
      content: <CoreEnumsCard />,
    },
    {
      indent: false,
      icon: 'warehouse',
      title: 'Warehouses',
      subtitle: 'Manage your warehouses',
      href: 'warehouses',
      content: (
        <>
          <WarehousesCard
            openCreateModal={() => setShowCreateWarehouseModal(true)}
            closeCreateModal={handleCloseCreateWarehouseModal}
          />
          {showCreateWarehouseModal && (
            <CreateWarehouseModal onClose={handleCloseCreateWarehouseModal} />
          )}
        </>
      ),
      // action:
      //   warehouses.data && warehouses.data.warehouses.length > 0
      //     ? {
      //         label: 'Add',
      //         handler: () => setShowCreateWarehouseModal(true),
      //       }
      //     : undefined,
    },
    {
      indent: false,
      icon: 'person',
      title: 'Members',
      href: 'members',
      content: <MembersCard />,
      // action: {
      //   label: 'Add',
      //   handler: () => window.alert('Not implemented yet'),
      // },
    },
    {
      indent: false,
      icon: 'diversity_3',
      title: 'Teams',
      href: 'teams',
      content: <TeamsCard />,
    },
  );

  if (gently) {
    items.push(
      {
        indent: false,
        icon: 'settings',
        title: 'Organization Settings',
        href: 'organization-settings',
        content: <OrganizationSettingsCard />,
        noWrapper: true,
      },
      {
        indent: false,
        icon: 'email',
        title: 'Azure Email Ingestion',
        subtitle: 'Configure email ingestion from your Azure tenant',
        href: 'azure-email-ingest',
        content: <AzureEmailIngestCard />,
        noWrapper: true,
      },
      {
        indent: false,
        icon: 'vpn_key',
        title: 'Modules',
        subtitle: `Here you can setup and manage your modules.`,
        href: 'modules',
        content: <ModulesConfig />,
      },
      {
        indent: false,
        icon: 'settings',
        title: 'Known Domains',
        subtitle: 'Configure internal/SSO domains for user authentication',
        href: 'known-domains',
        content: <KnownDomainsCard />,
        noWrapper: true,
      },
      {
        indent: false,
        icon: 'bug_report',
        title: 'Trigger Sentry Error',
        subtitle: 'Intentionally throw a frontend error to test Sentry',
        href: 'sentry-test',
        content: (
          <MDBox>
            <MDBox mb={1}>This will throw an error on click.</MDBox>
            <button
              onClick={() => {
                window.alert(
                  'Triggering a test error to verify Sentry in production...',
                );
                throw new Error('Intentional frontend error: Sentry test');
              }}
              style={{
                padding: '8px 12px',
                borderRadius: 8,
                background: '#ef4444',
                color: 'white',
              }}
            >
              Trigger Error
            </button>
          </MDBox>
        ),
        noWrapper: true,
      },
      {
        indent: false,
        icon: 'timer',
        title: 'Test Timeout',
        subtitle: 'Call backend to delay response by specified milliseconds',
        href: 'test-timeout',
        content: (
          <MDBox display="flex" alignItems="center" gap={2}>
            <TextField
              label="Seconds"
              type="number"
              value={seconds}
              onChange={(e) => setSeconds(e.target.value)}
              inputProps={{ min: 0 }}
              size="small"
              sx={{ width: 180 }}
            />
            <TextField
              label="Milliseconds"
              type="number"
              value={milliseconds}
              onChange={(e) => setMilliseconds(e.target.value)}
              inputProps={{ min: 0, max: 999 }}
              size="small"
              sx={{ width: 180 }}
            />
            <MDButton
              color="info"
              disabled={testTimeout.isLoading}
              onClick={() => {
                const s = parseInt(seconds, 10);
                const msPart = parseInt(milliseconds, 10);
                if (
                  Number.isNaN(s) ||
                  s < 0 ||
                  Number.isNaN(msPart) ||
                  msPart < 0 ||
                  msPart > 999
                ) {
                  window.alert(
                    'Please enter valid non-negative integers. Milliseconds must be between 0 and 999.',
                  );
                  return;
                }
                const ms = s * 1000 + msPart;
                const max = 15 * 60 * 1000;
                if (ms > max) {
                  window.alert('Maximum allowed is 15 minutes.');
                  return;
                }
                testTimeout.mutate({ ms });
              }}
            >
              {testTimeout.isLoading ? 'Waiting…' : 'Trigger Timeout'}
            </MDButton>
          </MDBox>
        ),
        noWrapper: true,
      },
      {
        indent: false,
        icon: 'bell',
        title: 'Demo Notifications',
        subtitle: 'Reset demo notifications to initial state',
        href: 'demo-notifications',
        content: (
          <MDBox>
            <MDBox mb={1}>
              This will delete all demo notifications and create a fresh one
              with the early payment discount alert.
            </MDBox>
            <MDButton
              color="info"
              disabled={reseedNotifications.isPending}
              onClick={() => reseedNotifications.mutate()}
            >
              {reseedNotifications.isPending
                ? 'Reseeding...'
                : 'Reset Demo Notifications'}
            </MDButton>
            {reseedNotifications.isSuccess && (
              <MDBox mt={1} color="success.main">
                Notifications reset successfully!
              </MDBox>
            )}
          </MDBox>
        ),
        noWrapper: true,
      },
    );
  }

  if (isModuleEnabled(ProductModule.FAMOUS_INTEGRATION)) {
    items.push({
      indent: true,
      icon: 'inventory',
      title: 'FAMOUS™',
      subtitle: 'FAMOUS™ integration settings',
      href: 'famous',
      content: <FamousConfigCard />,
    });
  }

  if (isModuleEnabled(ProductModule.ORDERFUL_EDI)) {
    items.push({
      indent: true,
      icon: 'inventory',
      title: 'EDI',
      subtitle: 'EDI settings',
      href: 'edi',
      content: <OrderfulEdiConfigCard />,
    });
  }

  const currentItem = items.find((item) => item.href === activeItem);

  // Transform items into NavigationSections for PageNavigator
  const navigationSections: NavigationSection[] = useMemo(() => {
    return items.map((item) => ({
      id: item.href,
      label: item.title,
      icon: getIconComponent(item.icon),
      ref: { current: null },
      hasData: true,
      isExpanded: false,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  return (
    <DashboardLayout>
      <div className="flex gap-3 mt-4">
        {/* Main Content */}
        <div className="flex-1 min-w-0">
          <MDBox>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <MDBox mb={3}>
                  <Grid container spacing={3}>
                    {currentItem && (
                      <SettingsCardContainer
                        id={currentItem.href}
                        title={currentItem.title}
                        subtitle={currentItem.subtitle}
                        action={currentItem.action}
                        noWrapper={currentItem.noWrapper}
                      >
                        {currentItem.content}
                      </SettingsCardContainer>
                    )}
                  </Grid>
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
        </div>

        {/* Page Navigator Sidebar */}
        <PageNavigator
          sections={navigationSections}
          onNavigate={handleItemClick}
          mode="sidebar"
          positioning="static"
          storageKey="settingsOrganizationNavigator"
          title="Organization Settings"
        />
      </div>

      {showCreateCategoryModal ? (
        <CreateCategoryModal onClose={handleCloseCreateCategoryModal} />
      ) : null}
    </DashboardLayout>
  );
}

export default SettingsOrganizationPage;
